import { Component, inject, OnInit, effect } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TenantConfigService } from './general/services/tenant-config.service';
import { ThemeInjectorService } from '@dispo-shared/shared-ui';
import { SseV2Service } from './general/services/sse/sse-v2.service';
import { UsersStoreService } from './general/services/users-store.service';
import { IconsComponent } from '@dispo-shared/shared-ui';
import { RouterOutlet } from '@angular/router';
import { PrimeNG } from 'primeng/config';
import { TranslateService } from '@ngx-translate/core';
import { BuildInfoService } from './general/services/build-info/build-info.service';
import { ToastService } from './general/services/toast.service';
import { Store } from '@ngrx/store';
import { AuthState } from './general/store/reducers/auth.reducer';
import { RestoreSession } from './general/store/actions/auth.action';

@Component({
  selector: 'xyz-web-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, IconsComponent],
})
export class AppComponent implements OnInit {
  private buildInfoService = inject(BuildInfoService);
  private toastService = inject(ToastService);
  private authStore = inject<Store<AuthState>>(Store);

  private titleService = inject(Title);
  private tenantService = inject(TenantConfigService);
  private themeInjector = inject(ThemeInjectorService);
  private sseService = inject(SseV2Service);
  private userStoreService = inject(UsersStoreService);
  private translateService = inject(TranslateService);
  private primeNGConfig = inject(PrimeNG);

  favIcon: HTMLLinkElement | null = document.querySelector('#appIcon');

  constructor() {
    // Restore session from localStorage if tokens exist
    this.authStore.dispatch(new RestoreSession());
    // Set title
    this.titleService.setTitle('Tourfold | ' + this.tenantService.tenantMeta().name || '');
    // Set favicon
    if (this.favIcon) {
      if (this.tenantService.tenantConfig().favicon !== '') {
        this.favIcon.href = this.tenantService.tenantConfig().favicon;
      } else {
        this.favIcon.href = `./assets/img/favicons/tourfold.png`;
      }
    }

    // Inject tenant colors into PrimeNG theme and CSS variables
    // Watch for tenant config changes and update theme
    effect(() => {
      const colors = this.tenantService.tenantConfig().colors;

      if (colors) {
        // Inject CSS variables through theme system (uses style element, not direct DOM manipulation)
        this.themeInjector.injectTenantCSSVariables(colors);
        const primaryColor = colors['--primary-color'];
        if (primaryColor) {
          this.themeInjector.updateThemeWithTenantColors(primaryColor);
        }
      }
    });

    effect(
      () => {
        this.userStoreService.setConnected(this.sseService.isConnected());
      },
      { allowSignalWrites: true }
    );

    // Log build info to console
    console.log('Build id:', this.buildInfoService.buildId());

    // Watch for version changes and show notification (must be in constructor for injection context)
    effect(() => {
      const isNewVersion = this.buildInfoService.isNewVersionAvailable();
      if (isNewVersion) {
        this.showVersionUpdateNotification();
      }
    });

    // Add these lines to test
    // setTimeout(() => {
    //   console.log('🧪 Testing SSE connection...');
    //   this.sseService.connect();
    // }, 3000);
  }
  ngOnInit(): void {
    this.primeNGConfig.setTranslation({
      dayNames: this.translateService.instant('SHARED.DATE.WEEKDAYS'),
      dayNamesMin: this.translateService.instant('SHARED.DATE.WEEKDAYS_SHORT'),
      dayNamesShort: this.translateService.instant('SHARED.DATE.WEEKDAYS_SHORT'),
      monthNames: this.translateService.instant('SHARED.DATE.MONTHS'),
      monthNamesShort: this.translateService.instant('SHARED.DATE.MONTHS_SHORT'),
    });
  }

  private showVersionUpdateNotification(): void {
    // Show toast notification
    this.toastService.info(
      this.translateService.instant('NEW_VERSION_AVAILABLE.MESSAGE'),
      this.translateService.instant('NEW_VERSION_AVAILABLE.TITLE'),
      {
        timeOut: 10000, // 10 seconds
        closeButton: true,
        progressBar: true,
        positionClass: 'toast-bottom-right',
        enableHtml: true,
      }
    );

    // Acknowledge the update automatically after showing the toast
    this.buildInfoService.acknowledgeVersionUpdate();
  }
}
